import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';

import { Box } from 'src/components/UI/Box';
import { ProtectedPassword } from 'src/components/UI/Input/ProtectedPassword';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import Typography from 'src/components/Typography';

import { Yup } from 'src/helpers/Yup';

import useLoading from 'src/helpers/useLoading';
import { Button } from 'src/components/UI/Button';
import { useAssistantContext } from 'src/providers/AssistantProvider';
import { AssistantService } from 'src/services/AssistantService';
import { FormControl } from 'src/components/UI/FormControl';
import { FormErrorMessage } from 'src/components/UI/FormErrorMessage';
import { Flex } from 'src/components/UI/Flex';
import { Checkbox } from 'src/components/UI/Checkbox';
import { TermsOfUse, TermsOfUseService } from 'src/services/termsOfUse';
import { MEVO_PRIVACY_URL } from 'src/helpers/defaultValues';
import { omit } from 'lodash';
import { TabsHeader } from './TabsHeader';
import { TABS_INDEX } from './AssistantSignUp';

interface CreatePasswordForm {
  password: string;
  confirmPassword: string;
  isPasswordProtected: boolean;
  termsOfUse: boolean;
  acceptedNewsLetter: boolean;
}

interface ProfessionalDataProps {
  onCreatAccount(): void;
  tabClick(index: number): void;
}

const initialValues: CreatePasswordForm = {
  password: '',
  confirmPassword: '',
  isPasswordProtected: false,
  termsOfUse: false,
  acceptedNewsLetter: false,
};

const createPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Informe uma senha válida.')
    .min(8, 'Sua senha deve ter no mínimo 8 caracteres')
    .max(16, 'Sua senha deve ter no máximo 16 caracteres')
    .test('validate password', 'Senha não atende os padrões de segurança.', function (password, context): boolean {
      return context.parent.isPasswordProtected;
    }),
  confirmPassword: Yup.string()
    .required('Informe uma senha de confirmação válida.')
    .min(8, 'Sua senha deve ter no mínimo 8 caracteres')
    .max(16, 'Sua senha deve ter no máximo 16 caracteres')
    .oneOf([Yup.ref('password'), ''], 'A senha e senha de confirmação devem ser iguais.'),
  isPasswordProtected: Yup.bool().required(),
  termsOfUse: Yup.bool().oneOf(
    [true],
    'Para prosseguir com o cadastro, você precisa realizar o consentimento da Política de Privacidade e Termos de Uso.'
  ),
  acceptedNewsLetter: Yup.bool().nullable(),
});

export function CreatePassword(props: ProfessionalDataProps) {
  const { createAssistantFormData, updateCreateAssistantFormData } = useAssistantContext();
  const { openToast } = useFeedbackContext();
  const [termsOfUse, setTermsOfUse] = useState<TermsOfUse | null>(null);

  const [loading, runWithLoading] = useLoading();

  useEffect(() => {
    const run = async () => {
      try {
        setTermsOfUse(await TermsOfUseService.getTermsOfUse());
      } catch (error) {
        openToast('Erro ao buscar os Termos de Uso. Tente novamente em instantes.', {
          type: 'error-outline',
          duration: 4000,
        });
      }
    };

    run();
  }, [openToast]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createPasswordSchema,
    validateOnChange: false,
    async onSubmit(values, formik) {
      runWithLoading(async () => {
        try {
          if (!termsOfUse) return;

          formik.setSubmitting(false);

          const term = {
            accepted: true,
            ...omit(termsOfUse, '_id', 'active'),
          };

          updateCreateAssistantFormData({
            password: values.password,
          });

          await AssistantService.createUser({
            ...createAssistantFormData,
            consentTerm: term,
            password: values.password,
          });

          props.onCreatAccount();
        } catch (e: any) {
          openToast(e.response.data.message, { type: 'error-outline' });
        }
      });
    },
  });

  const setFieldValue = useMemo(() => formik.setFieldValue, [formik.setFieldValue]);
  const validateField = useMemo(() => formik.validateField, [formik.validateField]);

  const updatePasswordFormValidation = useCallback(
    async (value: boolean) => {
      await setFieldValue('isPasswordProtected', value);
      await validateField('password');
    },
    [setFieldValue, validateField]
  );

  return (
    <form name={'create-password'} onSubmit={formik.handleSubmit}>
      <Box>
        <Box mb={'20px'}>
          <Typography model="h2" weight="bold" textAlign="center">
            Olá, {createAssistantFormData.fullName}
          </Typography>
          <Typography model="h3" weight="regular" textAlign="center">
            Complete seu cadastro
          </Typography>
        </Box>
        <TabsHeader tabClick={props.tabClick} currentIndex={TABS_INDEX.CREATE_PASSWORD} isFirstStepCompleted={true} />

        <Box mb={'20px'}>
          <Typography model={'body'} size={'large'} weight={'bold'}>
            Senha de acesso
          </Typography>
        </Box>

        <Box m={'20px 0'}>
          <ProtectedPassword
            label={'senha*'}
            errorMessage={formik.errors.password || formik.errors.isPasswordProtected}
            isInvalid={!!formik.errors.password || !!formik.errors.isPasswordProtected}
            placeholder={'Digite a senha'}
            value={formik.values.password}
            onChange={formik.handleChange}
            showPasswordProtectionAttributes={true}
            onProtected={updatePasswordFormValidation}
            name={'password'}
            onBlur={() => formik.validateField('password')}
            autoFocus
          />
        </Box>

        <Box m={'20px 0'}>
          <ProtectedPassword
            label={'Confirmar senha*'}
            errorMessage={formik.errors.confirmPassword}
            isInvalid={!!formik.errors.confirmPassword}
            placeholder={'Digite a senha de confirmação'}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            name={'confirmPassword'}
            onBlur={() => formik.validateField('confirmPassword')}
          />
        </Box>

        <Box mt={'20px'} mb={'16px'}>
          <FormControl isInvalid={!!formik.errors.termsOfUse} mb={'20px'}>
            <Flex>
              <Checkbox size={'sm'} mr={'10px'} name="termsOfUse" onChange={formik.handleChange} />
              <Typography model={'body'} size={'normal'} weight={'regular'}>
                Ao informar meus dados, eu concordo com a{' '}
                <a href={MEVO_PRIVACY_URL} target={'_blank'} rel={'noreferrer'}>
                  <u>Política de Privacidade</u>
                </a>{' '}
                e com os{' '}
                <a href={termsOfUse?.termUrl} target={'_blank'} rel={'noreferrer'}>
                  <u>Termos de Uso</u>
                </a>
              </Typography>
            </Flex>

            <FormErrorMessage textTransform={'uppercase'}>{formik.errors.termsOfUse}</FormErrorMessage>
          </FormControl>

          <Checkbox
            size={'sm'}
            name="acceptedNewsLetter"
            onChange={formik.handleChange}
            isChecked={formik.values.acceptedNewsLetter}
          >
            <Typography model={'body'} size={'normal'} weight={'regular'}>
              Aceito receber novidades e informações da Mevo Saúde em meu email e celular
            </Typography>
          </Checkbox>
        </Box>

        <Button h={'60px'} w={'100%'} mb={'22px'} type={'submit'} isLoading={loading} isDisabled={loading}>
          Finalizar Cadastro
        </Button>
      </Box>
    </form>
  );
}
